import { useState, useCallback, useRef, useEffect } from 'react';
import { useNodeStore } from '../stores/nodeStore';
import { AppNode, NodeType, NodeStatus } from '../types';

export function useNodeOperations(saveHistory: () => void) {
    const nodes = useNodeStore((s) => s.nodes);
    const addNode = useNodeStore((s) => s.addNode);
    const updateNode = useNodeStore((s) => s.updateNode);
    const deleteNodes = useNodeStore((s) => s.deleteNodes);
    const nodesRef = useRef(nodes);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);

    const [assetHistory, setAssetHistory] = useState<any[]>([]);

    const handleAssetGenerated = useCallback((type: 'image' | 'video' | 'audio', src: string, title: string) => {
        setAssetHistory(h => {
            const exists = h.find(a => a.src === src);
            if (exists) return h;
            return [{ id: `a-${Date.now()}`, type, src, title, timestamp: Date.now() }, ...h];
        });
    }, []);

    const handleAddNode = useCallback((type: NodeType, position?: { x: number; y: number }, initialData?: any) => {
        if (type === NodeType.IMAGE_EDITOR) {
            return 'open_sketch_editor';
        }

        try { saveHistory(); } catch (e) { }

        const defaults: any = {
            model: type === NodeType.VIDEO_GENERATOR ? 'veo-3.1-fast-generate-preview' :
                   type === NodeType.VIDEO_ANALYZER ? 'gemini-3-pro-preview' :
                   type === NodeType.AUDIO_GENERATOR ? 'gemini-2.5-flash-preview-tts' :
                   type === NodeType.MEDIA_UPLOAD ? undefined :
                   type.includes('IMAGE') ? 'gemini-2.5-flash-image' :
                   'gemini-3-pro-preview',
            aspectRatio: (type === NodeType.IMAGE_GENERATOR || type === NodeType.VIDEO_GENERATOR) ? '16:9' : undefined,
            generationMode: type === NodeType.VIDEO_GENERATOR ? 'DEFAULT' : undefined,
            ...initialData
        };

        const typeMap: Record<string, string> = {
            [NodeType.PROMPT_INPUT]: '创意描述',
            [NodeType.MEDIA_UPLOAD]: '媒体上传',
            [NodeType.IMAGE_GENERATOR]: '图片生成',
            [NodeType.VIDEO_GENERATOR]: '视频生成',
            [NodeType.AUDIO_GENERATOR]: '灵感音乐',
            [NodeType.VIDEO_ANALYZER]: '视频分析',
            [NodeType.IMAGE_EDITOR]: '图像编辑'
        };

        const safeX = position?.x ?? 100;
        const safeY = position?.y ?? 100;

        const newNode: AppNode = {
            id: `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
            type,
            x: isNaN(safeX) ? 100 : safeX,
            y: isNaN(safeY) ? 100 : safeY,
            width: 420,
            title: typeMap[type] || '未命名节点',
            status: NodeStatus.IDLE,
            data: defaults,
            inputs: []
        };

        addNode(newNode);
        return newNode.id;
    }, [addNode, saveHistory]);

    const handleNodeUpdate = useCallback((id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => {
        updateNode(id, data, size, title);
        if (data.image) handleAssetGenerated('image', data.image, title || 'Image');
        if (data.videoUri) handleAssetGenerated('video', data.videoUri, title || 'Video');
        if (data.audioUri) handleAssetGenerated('audio', data.audioUri, title || 'Audio');
    }, [updateNode, handleAssetGenerated]);

    const handleNodeDelete = useCallback((id: string) => {
        saveHistory();
        deleteNodes([id]);
    }, [deleteNodes, saveHistory]);

    const handleExtractImage = useCallback((nodeId: string, image: string) => {
        const sourceNode = nodesRef.current.find(n => n.id === nodeId);
        if (!sourceNode) return;
        const newX = sourceNode.x + 500;
        const newY = sourceNode.y;
        handleAddNode(NodeType.MEDIA_UPLOAD, { x: newX, y: newY }, { image });
    }, [handleAddNode]);

    return {
        handleAddNode,
        handleNodeUpdate,
        handleNodeDelete,
        handleExtractImage,
        handleAssetGenerated,
        assetHistory,
        setAssetHistory,
    };
}
